import React from 'react';
import isInt from 'validator/lib/isInt';
import {
  Button,
  Form,
  FormField,
  Grid,
  Label,
  Message,
  Segment,
  Table,
} from 'semantic-ui-react';
import { Field, Form as FinalForm } from 'react-final-form';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  businessInfoSelector,
  searchSelector,
  submitSelector,
} from 'state/restoreDeletedBusiness/selectors';
import {
  doRestoreBusiness,
  doSearchBusiness,
} from 'state/restoreDeletedBusiness/actions';
import { requiredStringValidation } from 'utils/formUtils';
import ValidatedFormInput from 'components/ValidatedFormInput';
import LogModal from 'pages/RestoreDeletedBusiness/LogModal';

function validateBusinessId(value) {
  if (!value || String(value).trim() === '') {
    return 'Business ID is required';
  }
  if (!isInt(String(value), { min: 1 })) {
    return 'Business ID must be a positive integer';
  }
  return undefined;
}

function BusinessInfoTable({ businessInfo }) {
  if (!businessInfo || !businessInfo.data) return null;
  const b = businessInfo.data;
  return (
    <Table definition celled>
      <Table.Body>
        <Table.Row>
          <Table.Cell>ID</Table.Cell>
          <Table.Cell>{b.id || '-'}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Name</Table.Cell>
          <Table.Cell>{b.name || '-'}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Email</Table.Cell>
          <Table.Cell>{b.email || '-'}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Status</Table.Cell>
          <Table.Cell>
            {b.is_frozen ? (
              <Label color="red">Frozen</Label>
            ) : (
              <Label color="green">Active</Label>
            )}
          </Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table>
  );
}

BusinessInfoTable.propTypes = {
  businessInfo: PropTypes.object,
};

BusinessInfoTable.defaultProps = {
  businessInfo: null,
};

function RestoreForm({ onSubmit, isFrozen }) {
  const submitState = useSelector(submitSelector);
  return (
    <FinalForm
      onSubmit={onSubmit}
      render={({ handleSubmit, submitting }) => (
        <Form onSubmit={handleSubmit}>
          <FormField>
            <label htmlFor="ticket-link">Request Ticket Link</label>
            <Field
              id="ticket-link"
              name="ticket_link"
              component={ValidatedFormInput}
              validate={requiredStringValidation()}
              placeholder="https://go-jek.atlassian.net/browse/XXX"
            />
          </FormField>
          <FormField>
            <label htmlFor="notes">Notes</label>
            <Field
              id="notes"
              name="notes"
              component={ValidatedFormInput}
              validate={requiredStringValidation()}
              placeholder="Merchant requested cancel"
            />
          </FormField>
          {submitState.error && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{submitState.error}</p>
            </Message>
          )}
          {submitState.success && (
            <Message positive>
              <p>Business restored successfully.</p>
            </Message>
          )}
          <Button
            positive
            type="submit"
            loading={submitState.isLoading}
            disabled={submitting || submitState.isLoading || !isFrozen}
          >
            Restore Business
          </Button>
        </Form>
      )}
    />
  );
}

RestoreForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isFrozen: PropTypes.bool.isRequired,
};

function MainPage({ onShowLogButtonClicked }) {
  const dispatch = useDispatch();
  const searchState = useSelector(searchSelector);
  const businessInfo = useSelector(businessInfoSelector);

  const onSearch = values => {
    dispatch(doSearchBusiness(values.business_id));
  };

  const onRestore = values => {
    if (businessInfo && businessInfo.data) {
      dispatch(
        doRestoreBusiness(
          businessInfo.data.id,
          values.ticket_link,
          values.notes
        )
      );
    }
  };

  return (
    <>
      <LogModal />
      <Button basic color="black" onClick={onShowLogButtonClicked}>
        Show Logs
      </Button>
      <Segment>
        <h3>Search Business</h3>
        <FinalForm
          onSubmit={onSearch}
          render={({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Grid columns="equal">
                <Grid.Column width={8}>
                  <FormField>
                    <label htmlFor="business-id">Business ID</label>
                    <Field
                      id="business-id"
                      name="business_id"
                      component={ValidatedFormInput}
                      validate={validateBusinessId}
                      placeholder="Enter Business ID"
                    />
                  </FormField>
                </Grid.Column>
                <Grid.Column>
                  <Button
                    primary
                    type="submit"
                    loading={searchState.isLoading}
                    disabled={searchState.isLoading}
                    style={{ marginTop: '1.5em' }}
                  >
                    Search
                  </Button>
                </Grid.Column>
              </Grid>
            </Form>
          )}
        />
        {searchState.error && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{searchState.error}</p>
          </Message>
        )}
      </Segment>

      {businessInfo && (
        <Segment>
          <h3>Business Information</h3>
          <BusinessInfoTable businessInfo={businessInfo} />
          <h3>Restore Details</h3>
          <RestoreForm
            onSubmit={onRestore}
            isFrozen={
              !!businessInfo.data && businessInfo.data.is_frozen === true
            }
          />
        </Segment>
      )}
    </>
  );
}

MainPage.propTypes = {
  onShowLogButtonClicked: PropTypes.func.isRequired,
};

export default MainPage;
